import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Download,
  ListChecks,
  Pencil,
  Plus,
  RefreshCcw,
  Sliders,
  TrendingUp,
} from 'lucide-react';
import clsx from 'clsx';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';
import { SelectField } from '../components/ui/Field';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  FormEditDrawer,
  ModerationChip,
  PeriodSelector,
  SubmissionModerationDrawer,
  FEEDBACK_AXIS_LABEL,
  FEEDBACK_AXES_ORDER,
  FEEDBACK_MODERATION_FILTERS,
  formatPeriodLabel,
  formatScore,
  lastNPeriodKeys,
  type FeedbackAdminAggregatesResponse,
  type FeedbackAdminListResponse,
  type FeedbackAdminSubmission,
  type FeedbackFormRow,
  type FeedbackFormsResponse,
  type FeedbackModerationStatus,
  type FormEditDrawerMode,
} from '../components/feedback';

export const Route = createFileRoute('/_authed/hr/feedback')({
  component: HrFeedbackPage,
});

type Tab = 'submissions' | 'aggregates' | 'forms';

const TAB_OPTIONS: ReadonlyArray<{ value: Tab; label: string; icon: React.ReactNode }> = [
  { value: 'submissions', label: 'Submissions', icon: <ListChecks size={14} aria-hidden="true" /> },
  { value: 'aggregates', label: 'Aggregates', icon: <TrendingUp size={14} aria-hidden="true" /> },
  { value: 'forms', label: 'Forms', icon: <Sliders size={14} aria-hidden="true" /> },
];

const PAGE_SIZE = 25;

function HrFeedbackPage() {
  const hasRole = useAuthStore((s) => s.hasRole);
  // ADMIN_ROLES on the backend (apps/api/src/routes/feedback.ts) = super_admin
  // + academic_admin. Both surfaces (list pencil + detail page) use the same
  // gate for the moderation drawer.
  const canManage = hasRole('super_admin', 'academic_admin');
  const isSuperAdmin = hasRole('super_admin');
  const [tab, setTab] = useState<Tab>('submissions');

  return (
    <ListOrOutlet basePath="/hr/feedback">
    <ListShell
      title="Parent feedback"
      description="Raw monthly responses, per-teacher aggregates, and form configuration."
    >
      <div
        role="tablist"
        aria-label="Feedback admin sections"
        className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
      >
        {TAB_OPTIONS.map((opt) => {
          const active = tab === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(opt.value)}
              className={clsx(
                'inline-flex items-center gap-2 rounded-t-md px-4 py-2 text-sm font-medium transition-colors',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                active
                  ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                  : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
              )}
            >
              {opt.icon}
              {opt.label}
            </button>
          );
        })}
      </div>

      <div className="mt-5">
        {tab === 'submissions' ? (
          <SubmissionsTab canManage={canManage} />
        ) : null}
        {tab === 'aggregates' ? (
          <AggregatesTab isSuperAdmin={isSuperAdmin} />
        ) : null}
        {tab === 'forms' ? <FormsTab isSuperAdmin={isSuperAdmin} /> : null}
      </div>
    </ListShell>
    </ListOrOutlet>
  );
}

// =========================================================================
// Submissions tab
// =========================================================================

function SubmissionsTab({ canManage }: { canManage: boolean }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [periodKey, setPeriodKey] = useState<string>('');
  const [moderationStatus, setModerationStatus] = useState<
    'all' | FeedbackModerationStatus
  >('all');
  // Drawer stays mounted alongside the list so the pencil shortcut can edit
  // moderation in place without leaving the page. Row click navigates to the
  // dedicated detail surface instead.
  const [drawerSubmission, setDrawerSubmission] =
    useState<FeedbackAdminSubmission | null>(null);

  const periodOptions = useMemo(() => {
    const keys = lastNPeriodKeys(12);
    return [
      { value: '', label: 'All periods' },
      ...keys.map((k) => ({ value: k, label: formatPeriodLabel(k) })),
    ];
  }, []);

  const queryParams = useMemo(() => {
    const out: Record<string, string | number | undefined> = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (periodKey) out.periodKey = periodKey;
    if (moderationStatus !== 'all') out.moderationStatus = moderationStatus;
    return out;
  }, [page, periodKey, moderationStatus]);

  const listQuery = useQuery({
    queryKey: ['feedback', 'admin', 'submissions', queryParams] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAdminListResponse>('/api/feedback/admin/submissions', {
        signal,
        query: queryParams,
      }),
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const handleExport = () => {
    const params = new URLSearchParams();
    if (periodKey) params.set('periodKey', periodKey);
    if (moderationStatus !== 'all') params.set('moderationStatus', moderationStatus);
    const apiOrigin =
      (import.meta.env.VITE_API_ORIGIN as string | undefined) ??
      'https://api.babulilmonline.com';
    const url = `${apiOrigin}/api/feedback/admin/export${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    // Use anchor click so the browser respects the Content-Disposition.
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = '';
    anchor.rel = 'noopener';
    // The export endpoint authenticates via the same session cookie/
    // bearer token; we open it in the same tab so the auth context comes
    // along. The browser handles the download once the response arrives.
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[180px]">
          <SelectField
            label="Period"
            value={periodKey}
            onChange={(e) => {
              setPeriodKey(e.target.value);
              setPage(1);
            }}
            options={periodOptions}
          />
        </div>
        <div className="min-w-[180px]">
          <SelectField
            label="Moderation"
            value={moderationStatus}
            onChange={(e) => {
              setModerationStatus(
                e.target.value as 'all' | FeedbackModerationStatus,
              );
              setPage(1);
            }}
            options={FEEDBACK_MODERATION_FILTERS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </div>
        <div className="ml-auto">
          <Button
            variant="secondary"
            size="sm"
            icon={<Download size={14} aria-hidden="true" />}
            onClick={handleExport}
            disabled={(listQuery.data?.total ?? 0) === 0}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <GatedSection
        error={listQuery.error}
        title="Feedback submissions"
        onRetry={() => listQuery.refetch()}
      >
        {listQuery.isLoading ? (
          <Skeleton className="h-72 w-full" />
        ) : (listQuery.data?.items ?? []).length === 0 ? (
          <EmptyState
            title="No submissions yet for this filter"
            description="Try a different period or moderation status."
          />
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[color:var(--color-surface)]">
                    <tr>
                      <Th>Submitted</Th>
                      <Th>Child</Th>
                      <Th>Teacher</Th>
                      <Th align="right">Overall</Th>
                      <Th>Moderation</Th>
                      <Th>Comment</Th>
                      {canManage ? (
                        <Th align="right">
                          <span className="sr-only">Edit</span>
                        </Th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {(listQuery.data?.items ?? []).map((row) => (
                      <tr
                        key={row.id}
                        onClick={() =>
                          navigate({
                            to: '/hr/feedback/submissions/$submissionId',
                            params: { submissionId: row.id },
                          })
                        }
                        className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
                      >
                        <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                          {formatDate(row.submittedAt, { withTime: true })}
                          <div className="text-[10px] text-[color:var(--color-text-muted)]">
                            {formatPeriodLabel(row.periodKey)}
                          </div>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <p className="font-medium">{row.studentName ?? '—'}</p>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <p className="font-medium">{row.teacherName ?? '—'}</p>
                        </td>
                        <td className="px-4 py-2 align-middle text-right tabular-nums font-semibold">
                          {row.responses?.overall ?? '—'}
                          <span className="ml-0.5 text-[10px] text-[color:var(--color-text-muted)]">
                            /5
                          </span>
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <ModerationChip status={row.moderationStatus} />
                        </td>
                        <td className="px-4 py-2 align-middle">
                          <p className="line-clamp-1 max-w-[28ch] text-xs text-[color:var(--color-text-muted)]">
                            {row.comment ?? '—'}
                          </p>
                        </td>
                        {canManage ? (
                          <td className="px-4 py-2 align-middle text-right">
                            <div className="flex justify-end">
                              <EditIconButton
                                label={`Moderate submission from ${row.studentName ?? 'parent'}`}
                                title="Moderate"
                                onClick={() => setDrawerSubmission(row)}
                              />
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={listQuery.data?.total ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </GatedSection>

      <SubmissionModerationDrawer
        submission={drawerSubmission}
        canManage={canManage}
        onClose={() => setDrawerSubmission(null)}
        onModerated={() => {
          setDrawerSubmission(null);
          void listQuery.refetch();
        }}
      />
    </section>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}) {
  return (
    <th
      scope="col"
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

// =========================================================================
// Aggregates tab
// =========================================================================

function AggregatesTab({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [periodKey, setPeriodKey] = useState<string>(
    () => lastNPeriodKeys(6)[0] ?? '',
  );

  // Server-computed rollup. `GET /api/feedback/admin/aggregates` reads the
  // persisted `feedback_aggregates` table (last recomputed value), applying
  // the same withdrawn/hidden exclusions the aggregator enforces at write
  // time, and returns the canonical cross-batch rows already sorted by
  // overall average (desc). We rely on the server's numbers instead of
  // re-deriving them from raw submissions in the browser.
  const listQuery = useQuery({
    queryKey: ['feedback', 'admin', 'aggregates', periodKey] as const,
    enabled: Boolean(periodKey),
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAdminAggregatesResponse>(
        '/api/feedback/admin/aggregates',
        {
          signal,
          // Cross-form view: omit `formId` so every form's aggregates roll
          // up together, matching the previous behaviour.
          query: periodKey ? { periodKey } : {},
        },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const aggregates = listQuery.data?.items ?? [];

  const recomputeMutation = useMutation({
    mutationFn: async () =>
      apiRequest<{ ok: true; scanned: number; recomputed: number; errors: number }>(
        '/api/feedback/admin/recompute',
        {
          method: 'POST',
          body: periodKey ? { periodKey } : {},
        },
      ),
    onSuccess: (res) => {
      toast(
        `Recomputed ${res.recomputed} teacher${res.recomputed === 1 ? '' : 's'}${
          res.errors > 0 ? ` · ${res.errors} error${res.errors === 1 ? '' : 's'}` : ''
        }.`,
        { variant: res.errors > 0 ? 'info' : 'success' },
      );
      void queryClient.invalidateQueries({ queryKey: ['feedback', 'admin'] });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not recompute.',
        { variant: 'error' },
      ),
  });

  // Per-row Recompute is the in-place power-user shortcut for a single
  // teacher/period combination. Scoped recompute hits the same endpoint with
  // both `teacherId` and `periodKey` so D1 only touches that aggregate row.
  const scopedRecomputeMutation = useMutation({
    mutationFn: async (vars: { teacherId: string; teacherName: string }) =>
      apiRequest<{ ok: true; scanned: number; recomputed: number; errors: number }>(
        '/api/feedback/admin/recompute',
        {
          method: 'POST',
          body: { teacherId: vars.teacherId, ...(periodKey ? { periodKey } : {}) },
        },
      ).then((res) => ({ ...res, teacherName: vars.teacherName })),
    onSuccess: (res) => {
      toast(
        res.errors > 0
          ? `Recomputed ${res.teacherName} with ${res.errors} error${res.errors === 1 ? '' : 's'}.`
          : `Recomputed ${res.teacherName}.`,
        { variant: res.errors > 0 ? 'info' : 'success' },
      );
      void queryClient.invalidateQueries({ queryKey: ['feedback', 'admin'] });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not recompute teacher.',
        { variant: 'error' },
      ),
  });

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px]">
          <PeriodSelector value={periodKey} onChange={setPeriodKey} />
        </div>
        {isSuperAdmin ? (
          <div className="ml-auto">
            <Button
              size="sm"
              variant="secondary"
              icon={<RefreshCcw size={14} aria-hidden="true" />}
              onClick={() => recomputeMutation.mutate()}
              loading={recomputeMutation.isPending}
            >
              Recompute aggregates
            </Button>
          </div>
        ) : null}
      </div>

      <GatedSection
        error={listQuery.error}
        title="Aggregates"
        onRetry={() => listQuery.refetch()}
      >
        {listQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : aggregates.length === 0 ? (
          <EmptyState
            title={`No aggregates yet for ${formatPeriodLabel(periodKey)}`}
            description="No qualifying submissions yet for this period."
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--color-surface)]">
                  <tr>
                    <Th>Teacher</Th>
                    <Th align="right">Responses</Th>
                    {FEEDBACK_AXES_ORDER.map((axis) => (
                      <Th key={axis} align="right">
                        {FEEDBACK_AXIS_LABEL[axis]}
                      </Th>
                    ))}
                    {isSuperAdmin ? (
                      <Th align="right">
                        <span className="sr-only">Recompute</span>
                      </Th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {aggregates.map((row) => {
                    const isRowRecomputing =
                      scopedRecomputeMutation.isPending &&
                      scopedRecomputeMutation.variables?.teacherId === row.teacherId;
                    const teacherName = row.teacherName ?? '—';
                    return (
                      <tr
                        key={row.id}
                        onClick={() =>
                          navigate({
                            to: '/hr/feedback/teachers/$teacherId',
                            params: { teacherId: row.teacherId },
                            search: periodKey ? { periodKey } : {},
                          })
                        }
                        className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
                      >
                        <td className="px-4 py-2 align-middle">
                          <p className="font-medium">{teacherName}</p>
                        </td>
                        <td className="px-4 py-2 align-middle text-right tabular-nums">
                          {row.responsesCount}
                        </td>
                        {FEEDBACK_AXES_ORDER.map((axis) => {
                          const x100 = {
                            clarity: row.clarityAvgX100,
                            patience: row.patienceAvgX100,
                            communication: row.communicationAvgX100,
                            progress: row.progressAvgX100,
                            overall: row.overallAvgX100,
                          }[axis];
                          return (
                            <td
                              key={axis}
                              className="px-4 py-2 align-middle text-right tabular-nums"
                            >
                              {formatScore(x100)}
                            </td>
                          );
                        })}
                        {isSuperAdmin ? (
                          <td className="px-4 py-2 align-middle text-right">
                            <div className="flex justify-end">
                              <RecomputeIconButton
                                label={`Recompute ${teacherName}`}
                                loading={isRowRecomputing}
                                disabled={scopedRecomputeMutation.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  scopedRecomputeMutation.mutate({
                                    teacherId: row.teacherId,
                                    teacherName,
                                  });
                                }}
                              />
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </GatedSection>
    </section>
  );
}

interface RecomputeIconButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  label: string;
  loading?: boolean;
  disabled?: boolean;
}

function RecomputeIconButton({
  onClick,
  label,
  loading = false,
  disabled = false,
}: RecomputeIconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-busy={loading || undefined}
      disabled={disabled}
      title={label}
      className={clsx(
        'inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-text-muted)] transition-colors duration-base',
        'hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg)]',
        disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-[color:var(--color-text-muted)]',
      )}
    >
      <RefreshCcw size={14} aria-hidden="true" className={loading ? 'animate-spin' : undefined} />
    </button>
  );
}

// =========================================================================
// Forms tab
// =========================================================================

function FormsTab({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState<FormEditDrawerMode | null>(null);

  const formsQuery = useQuery({
    queryKey: ['feedback', 'forms'] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackFormsResponse>('/api/feedback/forms', { signal }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  // To know whether a form already has submissions (for the warning banner),
  // peek at admin/submissions count for each form. Cheap because v1 has 1
  // form; we limit to a tiny page-1 probe per form to avoid expensive scans.
  const hasSubmissionsQuery = useQuery({
    queryKey: ['feedback', 'forms', 'has-submissions'] as const,
    enabled: (formsQuery.data?.items.length ?? 0) > 0,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAdminListResponse>('/api/feedback/admin/submissions', {
        signal,
        query: { page: 1, pageSize: 1 },
      }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const hasAnySubmissions = (hasSubmissionsQuery.data?.total ?? 0) > 0;

  return (
    <section className="space-y-4">
      <GatedSection
        error={formsQuery.error}
        title="Feedback forms"
        onRetry={() => formsQuery.refetch()}
      >
        <header className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm text-[color:var(--color-text-muted)]">
            Configure the question set, visibility threshold, and edit window
            for the monthly form. v1 typically uses the single seeded form.
          </p>
          {isSuperAdmin ? (
            <Button
              size="sm"
              icon={<Plus size={14} aria-hidden="true" />}
              onClick={() => setDrawer({ kind: 'create' })}
            >
              Create form
            </Button>
          ) : null}
        </header>

        {formsQuery.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (formsQuery.data?.items ?? []).length === 0 ? (
          <EmptyState
            title="No forms configured"
            description={
              isSuperAdmin
                ? 'Create the first form to start collecting parent feedback.'
                : 'No forms have been created yet. Ask a super admin to set one up.'
            }
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {(formsQuery.data?.items ?? []).map((form) => (
              <FormCard
                key={form.id}
                form={form}
                hasSubmissions={hasAnySubmissions}
                canEdit={isSuperAdmin}
                onEdit={() =>
                  setDrawer({ kind: 'edit', form, hasSubmissions: hasAnySubmissions })
                }
              />
            ))}
          </ul>
        )}
      </GatedSection>

      <FormEditDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          void queryClient.invalidateQueries({ queryKey: ['feedback', 'forms'] });
        }}
      />
    </section>
  );
}

interface FormCardProps {
  form: FeedbackFormRow;
  hasSubmissions: boolean;
  canEdit: boolean;
  onEdit: () => void;
}

function FormCard({ form, hasSubmissions, canEdit, onEdit }: FormCardProps) {
  // The whole card is a Link to the form detail page. The Edit thresholds
  // button is layered on top with a higher z-index and stops propagation so
  // the in-card pencil shortcut still mounts the FormEditDrawer without
  // navigating away (mirrors the row pencil pattern in the other two tabs).
  return (
    <li className="relative rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] transition-colors hover:border-gold-500/60 hover:bg-[color:var(--color-surface)] focus-within:border-gold-500">
      <Link
        to="/hr/feedback/forms/$formId"
        params={{ formId: form.id }}
        aria-label={`Open ${form.title}`}
        className="block rounded-lg p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-bg)]"
      >
        <header className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-display text-base font-semibold leading-tight text-[color:var(--color-text)]">
              {form.title}
            </p>
            <p className="text-xs text-[color:var(--color-text-muted)]">
              {form.scope} · {form.cadence}
            </p>
          </div>
          <span
            className={clsx(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
              form.isActive
                ? 'border-[color:var(--color-success)]/40 bg-[color:var(--color-success)]/10 text-[color:var(--color-success)]'
                : 'border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]',
            )}
          >
            {form.isActive ? 'Active' : 'Inactive'}
          </span>
        </header>
        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
          <div>
            <dt className="uppercase tracking-wide text-[color:var(--color-text-muted)]">
              Visibility threshold
            </dt>
            <dd className="mt-0.5 font-medium">
              {form.minVisibleResponseCount} responses
            </dd>
          </div>
          <div>
            <dt className="uppercase tracking-wide text-[color:var(--color-text-muted)]">
              Edit window
            </dt>
            <dd className="mt-0.5 font-medium">{form.editWindowHours} hours</dd>
          </div>
          <div className="col-span-2">
            <dt className="uppercase tracking-wide text-[color:var(--color-text-muted)]">
              Questions
            </dt>
            <dd className="mt-0.5 font-medium">
              {form.questionSet?.length ?? 0} axes
              {hasSubmissions ? ' · already in use' : ''}
            </dd>
          </div>
        </dl>
        {canEdit ? <div className="mt-3 h-8" aria-hidden="true" /> : null}
      </Link>
      {canEdit ? (
        <div className="absolute bottom-4 right-4 z-[1]">
          <Button
            size="sm"
            variant="secondary"
            icon={<Pencil size={14} aria-hidden="true" />}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onEdit();
            }}
          >
            Edit thresholds
          </Button>
        </div>
      ) : null}
    </li>
  );
}
